import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Spinner, EmptyState } from "@/components/States";
import { Plus, Trash2, Star, PackageSearch, Save, Tags } from "lucide-react";
import { toast } from "sonner";

const Table = ({ children }) => <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white"><table className="w-full text-sm">{children}</table></div>;
const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;

export function AdminCategories() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-categories"], queryFn: () => api.get("/admin/categories").then((r) => r.data) });
  const empty = { name: "", icon: "carrot", color: "#16a34a", is_active: true };
  const [form, setForm] = useState(empty);
  const add = async () => {
    if (!form.name) return toast.error("Category name required");
    try { await api.post("/admin/categories", form); qc.invalidateQueries({ queryKey: ["a-categories"] }); qc.invalidateQueries({ queryKey: ["cats"] }); toast.success("Category added"); setForm(empty); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (c) => { await api.patch(`/admin/categories/${c.id}`, { is_active: !c.is_active }); qc.invalidateQueries({ queryKey: ["a-categories"] }); qc.invalidateQueries({ queryKey: ["cats"] }); };
  const del = async (id) => { if (!window.confirm("Delete this category?")) return; await api.delete(`/admin/categories/${id}`); qc.invalidateQueries({ queryKey: ["a-categories"] }); qc.invalidateQueries({ queryKey: ["cats"] }); toast.success("Deleted"); };
  if (!data) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="admin-categories-panel">
      <div className="flex flex-wrap items-end gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <input placeholder="Category name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-category-name" className="w-48 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <input placeholder="Icon (carrot, milk...)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="w-40 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <label className="flex items-center gap-2 text-sm text-slate-600">Color <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-12 rounded border border-slate-200" /></label>
        <button onClick={add} data-testid="admin-category-add" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Order</Th><Th>Name</Th><Th>Slug</Th><Th>Icon</Th><Th>Active</Th><Th></Th></tr></thead>
        <tbody>
          {data.map((c) => (
            <tr key={c.id} className="border-b border-slate-50" data-testid="admin-category-row">
              <Td className="text-slate-400">{c.order}</Td>
              <Td className="font-semibold text-slate-800"><span className="inline-flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: c.color }} />{c.name}</span></Td>
              <Td className="font-mono text-xs text-slate-500">{c.slug}</Td><Td className="text-slate-500">{c.icon}</Td>
              <Td><Switch checked={c.is_active} onCheckedChange={() => toggle(c)} /></Td>
              <Td><button onClick={() => del(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export function AdminStores() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["a-stores"], queryFn: () => api.get("/admin/stores").then((r) => r.data) });
  const patch = async (id, body) => { await api.patch(`/admin/stores/${id}`, body); qc.invalidateQueries({ queryKey: ["a-stores"] }); qc.invalidateQueries({ queryKey: ["stores"] }); toast.success("Updated"); };
  if (!data) return <Spinner />;
  const vis = { visible: "bg-brand-soft text-brand", visible_warning: "bg-amber-soft text-amber-warm", hidden: "bg-red-50 text-red-500" };
  return (
    <Table>
      <thead><tr className="border-b border-slate-100"><Th>Store</Th><Th>Area</Th><Th>Products</Th><Th>Orders</Th><Th>Visibility</Th><Th>Featured</Th><Th>Open</Th></tr></thead>
      <tbody>
        {data.map((s) => (
          <tr key={s.id} className="border-b border-slate-50" data-testid="admin-store-row">
            <Td className="font-semibold text-slate-800">{s.name}</Td><Td className="text-slate-500">{s.area || s.city || "-"}</Td>
            <Td>{s.products_count}</Td><Td>{s.orders_count}</Td>
            <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${vis[s.visibility] || "bg-slate-100 text-slate-500"}`}>{(s.visibility || "").replace("_", " ")}</span></Td>
            <Td><Switch checked={!!s.is_featured} onCheckedChange={(v) => patch(s.id, { is_featured: v })} data-testid="admin-store-featured-toggle" /></Td>
            <Td><Switch checked={!!s.is_accepting_orders} onCheckedChange={(v) => patch(s.id, { is_accepting_orders: v })} /></Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function AdminProducts() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data } = useQuery({ queryKey: ["a-products", q, page], queryFn: () => api.get("/admin/products", { params: { q, page, page_size: 40 } }).then((r) => r.data) });
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try { await api.patch(`/admin/products/${edit.id}`, { price: +edit.price, mrp: +edit.mrp, stock: +edit.stock }); qc.invalidateQueries({ queryKey: ["a-products"] }); toast.success("Product updated"); setEdit(null); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleFeat = async (p) => { await api.patch(`/admin/products/${p.id}`, { is_featured: !p.is_featured }); qc.invalidateQueries({ queryKey: ["a-products"] }); };
  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); qc.invalidateQueries({ queryKey: ["a-products"] }); toast.success("Deleted"); };
  if (!data) return <Spinner />;
  const pages = Math.max(1, Math.ceil(data.total / data.page_size));
  return (
    <div className="space-y-3" data-testid="admin-products-panel">
      <div className="flex items-center gap-2">
        <PackageSearch className="h-4 w-4 text-slate-400" />
        <input placeholder="Search products or brands" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="admin-product-search" className="w-64 rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        <span className="ml-auto text-xs text-slate-400">{data.total} products</span>
      </div>
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Product</Th><Th>Store</Th><Th>Price</Th><Th>MRP</Th><Th>Stock</Th><Th>Feat.</Th><Th></Th></tr></thead>
        <tbody>
          {data.products.map((p) => (
            <tr key={p.id} className="border-b border-slate-50" data-testid="admin-product-row">
              <Td className="font-semibold text-slate-800">{p.name} <span className="text-xs text-slate-400">· {p.unit}</span></Td>
              <Td className="text-slate-500">{p.store_name}</Td>
              {edit?.id === p.id ? (
                <>
                  <Td><input type="number" value={edit.price} onChange={(e) => setEdit({ ...edit, price: e.target.value })} className="w-20 rounded border border-slate-200 px-2 py-1" /></Td>
                  <Td><input type="number" value={edit.mrp} onChange={(e) => setEdit({ ...edit, mrp: e.target.value })} className="w-20 rounded border border-slate-200 px-2 py-1" /></Td>
                  <Td><input type="number" value={edit.stock} onChange={(e) => setEdit({ ...edit, stock: e.target.value })} className="w-16 rounded border border-slate-200 px-2 py-1" /></Td>
                  <Td>—</Td>
                  <Td><button onClick={save} data-testid="admin-product-save" className="inline-flex items-center gap-1 rounded bg-brand px-2 py-1 text-xs font-bold text-white"><Save className="h-3 w-3" /> Save</button></Td>
                </>
              ) : (
                <>
                  <Td className="font-mono">{rupee(p.price)}</Td><Td className="font-mono text-slate-400 line-through">{rupee(p.mrp)}</Td><Td>{p.stock}</Td>
                  <Td><button onClick={() => toggleFeat(p)} title="Toggle featured"><Star className={`h-4 w-4 ${p.is_featured ? "fill-amber-surge text-amber-surge" : "text-slate-300"}`} /></button></Td>
                  <Td><div className="flex gap-2"><button onClick={() => setEdit({ id: p.id, price: p.price, mrp: p.mrp, stock: p.stock })} className="text-xs font-semibold text-brand">Edit</button><button onClick={() => del(p.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></div></Td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40">Prev</button>
          <span className="text-sm text-slate-500">Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

export function AdminPayments() {
  const [method, setMethod] = useState("");
  const { data } = useQuery({ queryKey: ["a-payments", method], queryFn: () => api.get("/admin/payments", { params: { method } }).then((r) => r.data), refetchInterval: 10000 });
  if (!data) return <Spinner />;
  if (!data.payments.length) return <EmptyState icon={Tags} title="No payments yet" subtitle="Customer order payments will appear here." />;
  return (
    <div className="space-y-4" data-testid="admin-payments-panel">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-400">Collected</p><p className="mt-1 font-head text-xl font-black text-brand">{rupee(data.collected)}</p></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs text-slate-400">Pending (COD)</p><p className="mt-1 font-head text-xl font-black text-amber-warm">{rupee(data.pending)}</p></div>
        {Object.entries(data.by_method).slice(0, 2).map(([m, v]) => (
          <div key={m} className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs capitalize text-slate-400">{m}</p><p className="mt-1 font-head text-xl font-black text-slate-800">{rupee(v.amount)}</p><p className="text-[11px] text-slate-400">{v.count} orders</p></div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <select value={method} onChange={(e) => setMethod(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="">All methods</option><option value="cod">COD</option><option value="razorpay">Razorpay</option></select>
      </div>
      <Table>
        <thead><tr className="border-b border-slate-100"><Th>Order</Th><Th>Customer</Th><Th>Store</Th><Th>Amount</Th><Th>Method</Th><Th>Payment</Th><Th>Date</Th></tr></thead>
        <tbody>
          {data.payments.map((p) => (
            <tr key={p.id} className="border-b border-slate-50" data-testid="admin-payment-row">
              <Td className="font-semibold text-slate-800">#{p.order_no}</Td><Td className="text-slate-500">{p.customer_name}</Td><Td className="text-slate-500">{p.store_name}</Td>
              <Td className="font-mono">{rupee(p.total)}</Td><Td className="capitalize text-slate-500">{p.payment_method}</Td>
              <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${p.payment_status === "paid" ? "bg-brand-soft text-brand" : "bg-amber-soft text-amber-warm"}`}>{p.payment_status}</span></Td>
              <Td className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString("en-IN")}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
